import { useState } from 'react'
import { Check, Lock, Cloud, FileX2, Undo2, ArrowRight } from 'lucide-react'
import { apiClient, unwrap } from '@/lib/apiClient'
import type { LandingPersona } from '@/hooks/useLandingPersona'

interface Props {
  persona: LandingPersona
  onSignIn: () => void
}

interface Tier {
  id: 'solo' | 'pro' | 'empire'
  name: string
  price: number
  minutes: number
  description: string
  features: string[]
  popular: boolean
}

const TIERS: Tier[] = [
  {
    id: 'solo',
    name: 'Solo',
    price: 25,
    minutes: 30,
    description: 'For the owner who wants eyes on the business — without staring at dashboards.',
    features: [
      '30 voice minutes / month',
      'Dedicated phone number',
      'All 5 personas & voices',
      'Facebook Ads readouts',
      'Stripe MRR readouts',
      'Morning briefings',
    ],
    popular: false,
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 79,
    minutes: 200,
    description: 'For the owner who wants their chief of staff on call, all day.',
    features: [
      '200 voice minutes / month',
      'Dedicated phone number',
      'All 5 personas & voices',
      'Facebook Ads readouts',
      'Stripe MRR readouts',
      'Morning briefings',
      'Priority call routing',
    ],
    popular: true,
  },
  {
    id: 'empire',
    name: 'Empire',
    price: 199,
    minutes: 500,
    description: 'For the high-volume operator who lives on the phone.',
    features: [
      '500 voice minutes / month',
      'Dedicated phone number',
      'All 5 personas & voices',
      'Facebook Ads readouts',
      'Stripe MRR readouts',
      'Morning briefings',
      'Priority call routing',
      'Early access to new features',
    ],
    popular: false,
  },
]

const TRUST = [
  { Icon: Lock, label: 'Secured by Stripe' },
  { Icon: Cloud, label: 'Azure-hosted' },
  { Icon: FileX2, label: 'No contracts' },
  { Icon: Undo2, label: 'Cancel any time' },
]

export default function LandingPricing({ persona, onSignIn }: Props) {
  const [loading, setLoading] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function startCheckout(tierId: Tier['id']) {
    setError(null)
    setLoading(tierId)
    try {
      // Try the public checkout first; if the user is unauthenticated and the
      // endpoint isn't wired up (env price IDs missing), fall back to sign-in.
      const data = await unwrap<{ url: string }>(
        apiClient.post('/billing/public-checkout', { tier: tierId }),
      )
      if (data.url) {
        window.location.href = data.url
        return
      }
      throw new Error('No checkout URL returned')
    } catch (e) {
      // Fall back to MSAL sign-in — after auth, /billing handles checkout.
      setLoading(null)
      const msg = (e as Error).message
      if (
        msg.toLowerCase().includes('not configured') ||
        msg.toLowerCase().includes('unknown tier') ||
        msg.toLowerCase().includes('not found')
      ) {
        setError(
          'Public checkout not configured yet — please sign in to continue.',
        )
      }
      onSignIn()
    }
  }

  return (
    <section
      className="lp-section"
      id="pricing"
      style={{ background: 'rgba(255,255,255,0.015)' }}
    >
      <div className="lp-container">
        <div className="lp-section-header">
          <div className="lp-eyebrow">Pricing</div>
          <h2>
            Flat pricing. <span style={{ color: persona.primary }}>No surprises.</span>
          </h2>
          <p className="lp-subtitle">
            Every plan includes all five personas and all features. Your AI goes live the
            moment you sign up — a phone number provisioned, your agent online, your first
            briefing scheduled. Overage minutes are $0.30/min. Cancel any time, instantly.
          </p>
        </div>

        <div className="lp-pricing-grid">
          {TIERS.map((tier) => (
            <div
              key={tier.id}
              className="lp-card lp-price-card"
              data-popular={tier.popular}
              style={
                tier.popular
                  ? {
                      borderColor: persona.primary,
                      background: `linear-gradient(180deg, ${persona.bgTint} 0%, rgba(255,255,255,0.015) 100%)`,
                    }
                  : undefined
              }
            >
              {tier.popular && <div className="lp-popular-pill">Most Popular</div>}

              <h3 className="lp-price-name">{tier.name}</h3>
              <p className="lp-price-blurb">{tier.description}</p>

              <div className="lp-price-amount">
                <span className="dollar">$</span>
                <span className="number">{tier.price}</span>
                <span className="period">/month</span>
              </div>
              <div className="lp-price-minutes">
                {tier.minutes} voice minutes included
              </div>

              <ul className="lp-price-features">
                {tier.features.map((f) => (
                  <li key={f} className="lp-price-feature">
                    <Check size={16} className="check" strokeWidth={2.4} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <button
                className={tier.popular ? 'lp-btn-primary' : 'lp-btn-outline'}
                onClick={() => startCheckout(tier.id)}
                disabled={loading === tier.id}
                style={{
                  width: '100%',
                  justifyContent: 'center',
                  ...(tier.popular
                    ? {}
                    : {
                        borderColor: persona.primary,
                        color: persona.primary,
                      }),
                }}
              >
                {loading === tier.id ? (
                  <>Redirecting…</>
                ) : (
                  <>
                    Start with {tier.name} <ArrowRight size={14} />
                  </>
                )}
              </button>
            </div>
          ))}
        </div>

        {error && (
          <p
            style={{
              textAlign: 'center',
              fontSize: 13,
              color: persona.secondary,
              marginTop: 24,
            }}
          >
            {error}
          </p>
        )}

        <div className="lp-trust-strip">
          {TRUST.map(({ Icon, label }) => (
            <span key={label} className="lp-trust-item">
              <Icon size={14} />
              {label}
            </span>
          ))}
        </div>
      </div>
    </section>
  )
}
